import { Button } from "@package/subframe/components/Button";

import type { SyncStatus } from "@/lib/offline/syncService";

interface Props {
  syncStatus: SyncStatus;
  onRetry: () => void;
}

/**
 * Persistent reassurance banner. UX designer's recommendation: explicit "saved"
 * language so users feel safe to close the tab. The banner exists in three states:
 *
 * - Online + no pending: "Draft saved — will sync immediately." (nothing visible
 *   in offline-first sense; we hide the banner to reduce noise on the happy path.)
 * - Online + pending: "X items syncing…" with implicit progress.
 * - Offline: "Draft saved locally — will sync when you're back online."
 * - Errors: "Sync failed for X items" + retry button.
 */
export function SyncStatusBanner({ syncStatus, onRetry }: Props) {
  const { pendingCount, errorCount, isOnline } = syncStatus;

  if (errorCount > 0) {
    return (
      <div className="flex w-full items-center justify-between gap-3 border-b border-error-300 bg-error-50 px-4 py-2">
        <span className="text-caption font-caption text-error-700">
          Sync failed for {errorCount} item{errorCount === 1 ? "" : "s"}.
        </span>
        <Button variant="destructive-secondary" size="small" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="w-full border-b border-amber-200 bg-amber-50 px-4 py-2">
        <span className="text-caption font-caption text-amber-900">
          Draft saved locally — will sync when you're back online.
        </span>
      </div>
    );
  }

  if (pendingCount > 0) {
    return (
      <div className="w-full border-b border-primary-navy-100 bg-primary-navy-50 px-4 py-2">
        <span className="text-caption font-caption text-primary-navy-800">
          Syncing {pendingCount} item{pendingCount === 1 ? "" : "s"}…
        </span>
      </div>
    );
  }

  // Online + nothing pending — no banner.
  return null;
}
